'use client'

import type { FormEvent } from 'react'
import { useState } from 'react'
import { useTrackFormAction, useTrackFormStepViewed } from '@nwwp/segment-analytics'
import { LeadGateFields, useLeadGateFields } from '../../../chrome/LeadGateFields'
import { PhoneDisclosure } from '../../../lead-magnets/components/PhoneDisclosure'
import { getTrackingParams } from '../../../utils/tracking'
import { qualifiesForSpendCheckAdvisor } from '../_lib/qualification'
import { saveUnlockedState, setUnlockCookie } from '../_lib/unlock'
import type { CreateSpendCheckLeadFn, SpendCheckState } from '../types'

/**
 * Gate shown between the last step and the results.
 *
 * The consent block is the shared PhoneDisclosure, and phone is required because of it:
 * `nwwp_spend-check` is stamped TCPA CONFIRMED at Lead create
 * (packages/salesforce/src/mappers/tcpa-source-map.ts).
 */
export function LeadGate({
  createLead,
  state,
  onBack,
  onSuccess,
}: {
  createLead: CreateSpendCheckLeadFn
  state: SpendCheckState
  onBack: () => void
  /** `showAdvisorCta` must land in live React state — not only sessionStorage. */
  onSuccess: (showAdvisorCta: boolean) => void
}) {
  const gate = useLeadGateFields()
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const trackFormAction = useTrackFormAction()
  const trackFormStepViewed = useTrackFormStepViewed()

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    const contact = gate.contact
    if (!contact || submitting) {
      if (!contact) gate.revealErrors()
      return
    }
    const { annual_income: income, investable_assets_range: assets } = contact
    setSubmitting(true)
    setSubmitError(null)
    const tracking = getTrackingParams()
    try {
      const created = await createLead(contact, state, tracking)
      if (!created) throw new Error('Lead creation failed')
      const showAdvisorCta = qualifiesForSpendCheckAdvisor(income, assets)
      // Survives refresh so a return visit is not asked to submit again (duplicate Lead).
      setUnlockCookie()
      saveUnlockedState({
        ...state,
        showAdvisorCta,
      })
      // Scoring fields only — PII stays off Form Action (same as find-your-advisor).
      try {
        trackFormAction({
          questionName: 'annual_income',
          questionValue: income,
          formName: 'worthwhile-spend-check',
          stepName: 'lead',
        })
        trackFormAction({
          questionName: 'investable_assets_range',
          questionValue: assets,
          formName: 'worthwhile-spend-check',
          stepName: 'lead',
        })
        trackFormStepViewed({
          step: 'Submit',
          type: 'form',
          formName: 'worthwhile-spend-check',
          formAction: 'submitted',
          ...tracking,
          investable_assets_range: assets,
        })
      } catch (error) {
        console.warn('Spend Check Form Action tracking failed', { error })
      }
      onSuccess(showAdvisorCta)
    } catch {
      setSubmitError('Something went wrong. Please try again.')
      setSubmitting(false)
    }
  }

  return (
    <form noValidate onSubmit={handleSubmit} className="flex max-w-160 flex-col gap-6">
      <div>
        <h1 className="text-title-md text-gray-darker">Your results are ready</h1>
        <p className="mt-3 text-body text-neutral-darker">
          Share a few details to see how your spending lines up with what you value.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <LeadGateFields gate={gate} idPrefix="sc" assetsPlaceholder="Select" />
      </div>

      <PhoneDisclosure ctaText="See my results" className="text-body-xs" />

      {submitError ? (
        <p role="alert" className="text-body-sm text-red">
          {submitError}
        </p>
      ) : null}

      <div className="flex flex-col-reverse items-stretch justify-between gap-3 tablet:flex-row tablet:items-center">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="text-body-sm-bold text-green-dark underline disabled:opacity-40"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-full bg-green-lighter-2 px-7 py-3.5 text-body-sm-bold text-black uppercase disabled:opacity-40"
        >
          {submitting ? 'Loading…' : 'See my results'}
        </button>
      </div>
    </form>
  )
}
